import { useState, useEffect, useCallback, useRef } from 'react';
import notificationService from '@/services/notificationService';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// iOS Safari and some embedded browsers don't ship the Notification Web API.
// Guard against `ReferenceError: Can't find variable: Notification`.
const getNotifPermission = () => {
  if (typeof window !== 'undefined' && typeof window.Notification !== 'undefined') {
    return window.Notification.permission;
  }
  return 'default';
};

/**
 * Hook for managing HR notifications
 */
export const useHRNotifications = (user) => {
  const [permission, setPermission] = useState(getNotifPermission());
  const [isSupported, setIsSupported] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const checkIntervalRef = useRef(null);

  // Initialize notification service
  useEffect(() => {
    const init = async () => {
      setIsSupported(notificationService.isSupported());
      setPermission(getNotifPermission());
      
      // Register service worker
      await notificationService.registerServiceWorker();
    };
    
    init();
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    const granted = await notificationService.requestPermission();
    setPermission(granted ? 'granted' : 'denied');
    return granted;
  }, []);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/hr/notifications?user_id=${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const notifs = response.data.notifications || [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.is_read).length);
      
      return notifs;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }, [user?.id]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/hr/notifications/mark-all-read?user_id=${user.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [user?.id]);

  // Check for shift reminders
  const checkShiftReminder = useCallback(async () => {
    if (!user?.id || permission !== 'granted') return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get attendance config
      const configRes = await axios.get(`${API_BASE_URL}/hr/config`, { headers });
      const config = configRes.data;
      
      // Check if today is a working day
      const today = new Date().getDay();
      // Convert Sunday (0) to our format (6), and other days -1
      const dayIndex = today === 0 ? 6 : today - 1;
      if (!config.working_days.includes(dayIndex)) return;
      
      // Check if already marked attendance today
      const todayDate = new Date().toISOString().split('T')[0];
      const attendanceRes = await axios.get(
        `${API_BASE_URL}/hr/attendance/records?user_id=${user.id}&start_date=${todayDate}&end_date=${todayDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (attendanceRes.data.records.length > 0) return; // Already marked
      
      // Check time
      const now = new Date();
      const [shiftHour, shiftMin] = config.shift_start_time.split(':').map(Number);
      const shiftStart = new Date();
      shiftStart.setHours(shiftHour, shiftMin, 0, 0);
      
      const minutesUntilShift = (shiftStart - now) / (1000 * 60);
      
      // Show reminder 30 minutes before shift
      if (minutesUntilShift > 0 && minutesUntilShift <= 30) {
        const lastReminder = localStorage.getItem('lastShiftReminder');
        const todayKey = todayDate;
        
        if (lastReminder !== todayKey) {
          await notificationService.showShiftReminder(config.shift_start_time);
          localStorage.setItem('lastShiftReminder', todayKey);
        }
      }
      
      // Show late alert after grace period
      if (minutesUntilShift < -config.grace_period_minutes && minutesUntilShift > -config.late_threshold_minutes) {
        const lastLateAlert = localStorage.getItem('lastLateAlert');
        const todayKey = todayDate;
        
        if (lastLateAlert !== todayKey) {
          await notificationService.showLateAttendanceAlert();
          localStorage.setItem('lastLateAlert', todayKey);
        }
      }
      
    } catch (error) {
      console.error('Error checking shift reminder:', error);
    }
  }, [user?.id, permission]);

  // Set up periodic checks
  useEffect(() => {
    if (!user?.id || permission !== 'granted') return;
    
    // Initial check
    checkShiftReminder();
    fetchNotifications();
    
    // Check every 5 minutes for shift reminders
    checkIntervalRef.current = setInterval(() => {
      checkShiftReminder();
      fetchNotifications();
    }, 5 * 60 * 1000);
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [user?.id, permission, checkShiftReminder, fetchNotifications]);

  // Show notification helper
  const showNotification = useCallback(async (type, data) => {
    if (permission !== 'granted') return;
    
    switch (type) {
      case 'leave_request':
        return notificationService.showLeaveRequestNotification(
          data.employeeName,
          data.leaveType,
          data.days
        );
      case 'leave_status':
        return notificationService.showLeaveStatusNotification(
          data.status,
          data.leaveType
        );
      case 'streak_milestone':
        return notificationService.showStreakMilestoneNotification(
          data.streakDays,
          data.rewardDescription
        );
      case 'salary_slip':
        return notificationService.showSalarySlipNotification(data.month);
      case 'admin_alert':
        return notificationService.showAdminAlert(
          data.title,
          data.message,
          data.alertType
        );
      case 'daily_summary':
        return notificationService.showDailySummaryNotification(
          data.present,
          data.late,
          data.absent,
          data.onLeave
        );
      default:
        return null;
    }
  }, [permission]);

  return {
    permission,
    isSupported,
    notifications,
    unreadCount,
    requestPermission,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    showNotification,
    hasPermission: permission === 'granted'
  };
};

export default useHRNotifications;
